import React, { useEffect, useMemo, useState } from 'react'
import { Layout, Menu, Drawer, Button } from 'antd'
import { MenuOutlined } from '@ant-design/icons'
import { NavLink, Redirect, Route, Switch, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  DashboardOutlined,
  TeamOutlined,
  BookOutlined,
  BarChartOutlined,
  MailOutlined
} from '@ant-design/icons'

import Spinner from '../../components/Spinner'
import DropDownNotification from '../../components/DropDownNotification'
import ProfileIcon from '../../components/ProfileIcon'
import { getAllNotifications } from '../../reducers/notificationsReducer'
import useMobile from '../../hooks/useMobile'

import AdminDashboard from './pages/AdminDashboard'
import AdminUsers from './pages/AdminUsers'
import AdminCourses from './pages/AdminCourses'
import AdminEmail from './pages/AdminEmail'

const { Sider, Content } = Layout

const adminLinks = [
  { key: '/admin/dashboard', icon: <DashboardOutlined />, to: '/admin/dashboard', label: 'Dashboard' },
  { key: '/admin/users', icon: <TeamOutlined />, to: '/admin/users', label: 'Users' },
  { key: '/admin/courses', icon: <BookOutlined />, to: '/admin/courses', label: 'Courses' },
  { key: '/admin/email', icon: <MailOutlined />, to: '/admin/email', label: 'Email' },
  { key: '/admin/analytics', icon: <BarChartOutlined />, to: '/admin/dashboard#analytics', label: 'Analytics' }
]

const AdminApp = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const { user } = useSelector((state) => state.auth)
  const isMobile = useMobile()

  const [collapsed, setCollapsed] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  useEffect(() => {
    dispatch(getAllNotifications())
  }, [dispatch])

  const selectedKey = useMemo(() => {
    const p = location.pathname
    if (p.startsWith('/admin/users')) return '/admin/users'
    if (p.startsWith('/admin/courses')) return '/admin/courses'
    if (p.startsWith('/admin/email')) return '/admin/email'
    if (p.startsWith('/admin/dashboard')) return '/admin/dashboard'
    return '/admin/dashboard'
  }, [location.pathname])

  const menuContent = (
    <>
      <div
        style={{
          cursor: 'pointer',
          padding: collapsed && !isMobile ? '16px 8px' : '16px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'hsl(210, 55%, 22%)',
          color: 'white',
          fontWeight: 700
        }}
        onClick={() => {
          if (isMobile) setMobileMenuOpen(false)
          window.location = '/'
        }}
      >
        {collapsed && !isMobile ? 'WA' : 'WiseAcademy Admin'}
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[selectedKey]}
        style={{ background: 'transparent', border: 'none' }}
        onClick={() => isMobile && setMobileMenuOpen(false)}
      >
        {adminLinks.map(({ key, icon, to, label }) => (
          <Menu.Item key={key} icon={icon}>
            <NavLink to={to}>{label}</NavLink>
          </Menu.Item>
        ))}
      </Menu>
    </>
  )

  if (!user) return <Spinner size="large" />

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {isMobile ? (
        <Drawer
          placement="left"
          onClose={() => setMobileMenuOpen(false)}
          visible={mobileMenuOpen}
          width={280}
          bodyStyle={{ padding: 0, background: 'hsl(210, 55%, 22%)', height: '100%' }}
          style={{ zIndex: 1050 }}
        >
          {menuContent}
        </Drawer>
      ) : (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={(v) => setCollapsed(v)}
          breakpoint="lg"
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            background: 'hsl(210, 55%, 22%)'
          }}
          theme="dark"
        >
          {menuContent}
        </Sider>
      )}

      <Layout
        style={{
          marginLeft: isMobile ? 0 : (collapsed ? 80 : 200),
          transition: 'margin-left .2s',
          width: isMobile ? '100%' : undefined
        }}
      >
        <header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0 16px',
            minHeight: 56,
            background: 'hsl(33, 100%, 50%)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isMobile && (
              <Button
                type="text"
                icon={<MenuOutlined style={{ fontSize: 20, color: '#fff' }} />}
                onClick={() => setMobileMenuOpen(true)}
                style={{ minWidth: 44, minHeight: 44, padding: 0 }}
              />
            )}
            <span style={{ fontWeight: 700, color: '#fff' }}>Admin</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DropDownNotification />
            <ProfileIcon />
          </div>
        </header>

        <Content
          style={{
            padding: isMobile ? '16px 12px' : '20px 32px',
            height: '100%',
            minHeight: isMobile ? 'calc(100vh - 56px)' : undefined
          }}
        >
          <Switch>
            <Route exact path="/admin/">
              <Redirect to="/admin/dashboard" />
            </Route>
            <Route path="/admin/dashboard" component={AdminDashboard} />
            <Route path="/admin/users" component={AdminUsers} />
            <Route path="/admin/courses" component={AdminCourses} />
            <Route path="/admin/email" component={AdminEmail} />
            <Route path="/admin/*">
              <Redirect to="/admin/dashboard" />
            </Route>
          </Switch>
        </Content>
      </Layout>
    </Layout>
  )
}

export default AdminApp
